/**
 * Nucery Bundle Builder — "Compose your edit"
 * Client-side edit cart: pick variants from collection-fed cards, build an
 * "edit" with live totals / tier rewards / perks / animations, then commit the
 * whole edit to the real Shopify cart via /cart/add.js.
 *
 * Tier discount %s are visual estimates only — real pricing is enforced by
 * Shopify Automatic Discounts the merchant sets up separately.
 *
 * Hooks: data-bb-* (no collision with the site cart drawer's data-nucery-*).
 */
(function () {
  'use strict';

  // Build marker — check `window.__nuceryBBVersion` in the browser console to
  // confirm THIS file is the one actually running (not a cached older copy).
  // Bump this string whenever the file changes so stale deploys are obvious.
  window.__nuceryBBVersion = 'advanced-2026-07-21a';

  // ---- Cart-exit safety net — installed ABOVE the single-instance guard ----
  // CRITICAL: this must run for EVERY loaded build, including one that bails at
  // the guard below because an older build already initialised. On a hot-reload
  // dev theme the OLD build's exit interceptor stays bound to `document` and
  // fires the "still unlocked" popup on the header cart click; the new build's
  // in-init safety net never gets a chance to install because the IIFE returns
  // first. So we install a self-contained watcher here, before the guard: it
  // records cart/drawer clicks and, if the exit modal opens right after one,
  // immediately cancels it — no matter which (old/new/stale) interceptor opened
  // it, because it acts on the shared modal DOM. Re-runs each load so the newest
  // build always refreshes it (own flag, not tied to __nuceryBBInit).
  (function installCartExitSafety() {
    // NOTE: intentionally NOT bailing on a persistent window flag. A guard like
    // `if (window.__nuceryBBCartSafe2) return;` makes a NEWER build (hot-reload)
    // bail so the OLDER, less-capable net keeps running — exactly the bug that
    // let the category-tile popup through. Installing every load is harmless (the
    // observers are idempotent) and guarantees the newest logic is always active.
    // The flag is still SET below purely as a "did it run" marker.
    window.__nuceryBBCartSafe2 = true;
    // The cart drawer is open when <html>/<body> carries `nucery-cart-drawer--open`
    // or the drawer root has `nucery-cart-drawer--is-open`. If it's open, ANY exit
    // popup is a false positive (cart toggle, category tile, close, backdrop…).
    function drawerOpen() {
      return document.documentElement.classList.contains('nucery-cart-drawer--open') ||
             document.body.classList.contains('nucery-cart-drawer--open') ||
             !!document.querySelector('.nucery-cart-drawer--is-open');
    }
    var lastCartClickAt = 0;
    document.addEventListener('click', function (e) {
      // Anything inside the cart drawer itself (category tiles, "Shop all",
      // close button, backdrop) counts as a cart/drawer interaction.
      if (e.target && e.target.closest && e.target.closest('.nucery-cart-drawer, [data-nucery-cart-open]')) {
        lastCartClickAt = Date.now();
        return;
      }
      var el = e.target && e.target.closest
        ? e.target.closest('a, button, [role="button"], [data-nucery-cart-open], [data-drawer-toggle], [data-cart-drawer], [data-cart-toggle]')
        : null;
      if (!el) return;
      var hay = [
        (el.getAttribute && el.getAttribute('href')) || '',
        el.className || '',
        el.id || '',
        (el.getAttribute && el.getAttribute('aria-controls')) || '',
        (el.getAttribute && el.getAttribute('aria-label')) || ''
      ].join(' ').toLowerCase();
      if (/cart|drawer|basket|bag/.test(hay) ||
          (el.matches && el.matches('[data-nucery-cart-open], [data-drawer-toggle], [data-cart-drawer], [data-cart-toggle], .js-nucery-cart-open, .js-drawer-open-cart, .js-drawer-open-nav'))) {
        lastCartClickAt = Date.now();
      }
    }, true);
    function cancelIfCartOpen(m) {
      // Cancel if the drawer is open right now (guaranteed false positive), OR the
      // modal opened within a moment of a cart/drawer click (covers the drawer
      // TOGGLE, which closes the drawer before the popup would show).
      if (m.classList.contains('open') && (drawerOpen() || (Date.now() - lastCartClickAt) < 1200)) {
        m.classList.remove('open');
        m.setAttribute('aria-hidden', 'true');
        var qv = document.querySelector('[data-bb-qv]');
        if (!qv || !qv.classList.contains('open')) document.documentElement.classList.remove('bb-qv-lock');
        return true;
      }
      return false;
    }
    function watch() {
      var m = document.querySelector('[data-bb-exit]');
      if (!m || !window.MutationObserver) return false;
      // Catch it even if it's already open the instant we attach.
      cancelIfCartOpen(m);
      new MutationObserver(function () { cancelIfCartOpen(m); })
        .observe(m, { attributes: true, attributeFilter: ['class', 'aria-hidden'] });
      return true;
    }
    if (!watch()) {
      var tries = 0;
      var iv = setInterval(function () { if (watch() || ++tries > 80) clearInterval(iv); }, 100);
    }
  })();

  // Single-instance guard. Bail ONLY if a prior run actually INITIALISED (i.e.
  // bound the handlers — marked by __nuceryBBInit). A prior run that merely
  // claimed the flag but never initialised — e.g. it executed before the section
  // existed on a hot-reload dev theme, then bailed — must NOT block this run, or
  // the builder would stay dead forever. In that case we take over cleanly (no
  // handlers were bound yet, so there's nothing to double up).
  if (window.__nuceryBBLoaded && window.__nuceryBBInit) return;
  window.__nuceryBBLoaded = true;

  var root, config, catalog;
  var qvEntry = null; // product entry currently shown in the quick-view modal
  // The edit is an ORDERED LIST of individual pieces (NOT a variantId→qty map).
  // Adding the same product twice makes TWO pieces, each with its own unique
  // `pid`, so the tray shows it as two cards and removing one removes exactly
  // that one — no qty collapsing, no "remove one wipes all", no resurrecting.
  var pieces = []; // [{ pid, variantId, productId, title, meta, image, price, compare }]
  var pieceSeq = 0; // monotonic id source for pieces
  var products = {}; // productId -> { product, chips:[], sel:{}, variant }
  var tiers = []; // sorted ascending by req
  var prevCount = 0;
  var prevTierIdx = -1;
  var prevTotal = 0;

  function qs(sel, ctx) {
    return (ctx || root).querySelector(sel);
  }
  function qsa(sel, ctx) {
    return Array.prototype.slice.call((ctx || root).querySelectorAll(sel));
  }

  function ensureRoot() {
    root = document.getElementById('NuceryBundleBuilder');
    // Flag the page so CSS can hide overlapping third-party widgets (e.g. the
    // Dondy WhatsApp chat bubble) only where the bundle builder is present.
    if (root) document.documentElement.classList.add('bb-has-builder');
    return root;
  }

  function readJSON(sel) {
    var el = root && root.querySelector(sel);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  /* ---------- money ---------- */
  function stripHtml(html) {
    var d = document.createElement('div');
    d.innerHTML = String(html == null ? '' : html);
    return (d.textContent || d.innerText || '').trim();
  }
  // Active display currency + base→active rate from Shopify's storefront
  // multi-currency. Liquid bakes prices in the SHOP base currency; the storefront
  // converts to the customer's chosen currency client-side via Shopify.currency.
  // Our prices aren't .money spans the theme converter would touch, so we convert
  // here the same way the theme does.
  function activeCurrency() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || config.currency || config.shopCurrency || 'USD';
  }
  function activeRate() {
    var r = (window.Shopify && window.Shopify.currency) ? parseFloat(window.Shopify.currency.rate) : NaN;
    return (isFinite(r) && r > 0) ? r : 1;
  }
  function formatMoneyRaw(cents) {
    cents = Number(cents) || 0;
    var shopCur = (config.shopCurrency || config.currency || 'USD').toString();
    var active = String(activeCurrency());
    var fmt = config.moneyFormat;
    var shopifyOk = window.Shopify && typeof Shopify.formatMoney === 'function' && fmt;
    // Viewing the base currency → use the theme's money format as-is.
    if (active.toUpperCase() === shopCur.toUpperCase()) {
      if (shopifyOk) return Shopify.formatMoney(cents, fmt);
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: active }).format(cents / 100);
      } catch (e) {
        return (cents / 100).toFixed(2) + ' ' + active;
      }
    }
    // Different display currency → convert base→active by the storefront rate.
    var converted = cents * activeRate();
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: active }).format(converted / 100);
    } catch (e) {
      return (converted / 100).toFixed(2) + ' ' + active;
    }
  }
  function money(cents) {
    return stripHtml(formatMoneyRaw(cents));
  }
  // Local copy of Shopify's money formatter — NOT converting and NOT hooked.
  // Produces the exact string the theme's money format yields (correct decimal /
  // thousands separators for the base currency), which is what a currency app
  // like Vitals expects to parse. Using Intl instead can emit "€54.00" for a
  // comma-decimal locale, which the app misreads as 5400 → wrong conversion.
  function formatMoneyLocal(cents, format) {
    var fmt = format || '${{amount}}';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    function withDelimiters(number, precision, thousands, decimal) {
      precision = (precision == null) ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) number = 0;
      var amt = (number / 100.0).toFixed(precision);
      var parts = amt.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var cs = parts[1] ? (decimal + parts[1]) : '';
      return dollars + cs;
    }
    var match = fmt.match(placeholder);
    var key = match ? match[1] : 'amount';
    var value;
    switch (key) {
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break;
      default: value = withDelimiters(cents, 2);
    }
    return fmt.replace(placeholder, value);
  }
  // Format an amount in the SHOP's base currency for a currency app to convert.
  // IMPORTANT: never route this through Shopify.formatMoney / theme.Currency —
  // on stores with a currency app those helpers are hooked to ALREADY convert,
  // which would make the app convert a second time. Uses the theme money format
  // via a local (non-hooked) formatter so the .money span holds the true base
  // amount in the exact format the app expects; the app converts it once.
  function moneyBase(cents) {
    cents = Number(cents) || 0;
    var fmt = config && config.moneyFormat;
    if (fmt) {
      // For WHOLE amounts, emit no decimals (e.g. "€54" not "€54.00"). On stores
      // whose base currency treats "." / "," as a THOUSANDS separator (EUR etc.),
      // a currency app can misread "54.00" as 5400 → wrong conversion. Dropping
      // the ".00" removes that ambiguity; fractional prices keep their decimals.
      if (cents % 100 === 0) {
        if (fmt.indexOf('amount_with_comma_separator') !== -1) {
          fmt = fmt.replace('amount_with_comma_separator', 'amount_no_decimals_with_comma_separator');
        } else if (fmt.indexOf('amount_with_apostrophe_separator') !== -1) {
          fmt = fmt.replace('amount_with_apostrophe_separator', 'amount_no_decimals');
        } else if (fmt.indexOf('amount_no_decimals') === -1) {
          fmt = fmt.replace('amount', 'amount_no_decimals');
        }
      }
      var out = stripHtml(formatMoneyLocal(cents, fmt));
      // Belt-and-suspenders: for whole amounts, strip any trailing ",00" / ".00"
      // that a money format might still emit, so a currency app can't read the
      // zero-decimals as a thousands group (€54.00 → 5400).
      if (cents % 100 === 0) out = out.replace(/([.,])0{2}(\D*)$/, '$2');
      return out;
    }
    var cur = (config && (config.shopCurrency || config.currency)) || 'USD';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur, minimumFractionDigits: (cents % 100 === 0 ? 0 : 2) }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2) + ' ' + cur;
    }
  }
  // Price as HTML. When a currency app (Vitals etc.) is enabled, wrap the base
  // amount in a <span class="money"> so the app can convert it to the shopper's
  // currency (it scans/observes .money elements). Otherwise return the normal
  // (storefront-rate) formatted text, escaped.
  function moneyEl(cents) {
    if (config && config.currencyApp) return '<span class="money">' + moneyBase(cents) + '</span>';
    return escapeHtml(money(cents));
  }

  function escapeHtml(s) {
    var d = document.createElement('div');
    d.textContent = s == null ? '' : String(s);
    return d.innerHTML;
  }
  function escapeAttr(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
  function str(key) {
    return (config.strings && config.strings[key] != null) ? config.strings[key] : '';
  }

  /* ---------- option swatch colours ---------- */
  var SWATCH_COLORS = {
    platinum: 'linear-gradient(135deg,#e9e9ec,#c9c9cf)',
    silver: 'linear-gradient(135deg,#e9e9ec,#c9c9cf)',
    'white gold': 'linear-gradient(135deg,#efeff2,#d6d6db)',
    gold: 'linear-gradient(135deg,#e7c987,#c69a4c)',
    'yellow gold': 'linear-gradient(135deg,#e7c987,#c69a4c)',
    '18k gold': 'linear-gradient(135deg,#e7c987,#c69a4c)',
    rose: 'linear-gradient(135deg,#e9c4b4,#cf9d8a)',
    'rose gold': 'linear-gradient(135deg,#e9c4b4,#cf9d8a)',
    bronze: 'linear-gradient(135deg,#c89b6a,#9c7240)',
    copper: 'linear-gradient(135deg,#d99b76,#b06a45)',
    champagne: 'linear-gradient(135deg,#f0e2c4,#d9c79c)',
    black: '#1d1d1d',
    white: '#fff',
    ivory: '#f4ecdd',
    cream: '#f4ecdd',
    pearl: 'radial-gradient(circle at 35% 30%,#fff,#e6e2da)',
    blue: '#2f5fbf',
    'navy blue': '#22335c',
    navy: '#22335c',
    'sky blue': '#7db4e6',
    teal: '#1f8a8a',
    green: '#3f8f5b',
    'emerald green': '#2f7a52',
    emerald: '#2f7a52',
    olive: '#7a7a3f',
    sage: '#a7b59a',
    red: '#b53245',
    burgundy: '#6b2233',
    wine: '#6b2233',
    pink: '#e6a0b5',
    purple: '#7a4f9c',
    grey: '#9a9a9a',
    gray: '#9a9a9a',
    brown: '#7a5230',
    beige: '#e3d6bf'
  };
  function colorFor(value) {
    return SWATCH_COLORS[String(value || '').trim().toLowerCase()] || null;
  }

  /* ---------- catalog → products map ---------- */
  function addProductEntry(p) {
    var entry = products[p.id];
    if (!entry) {
      entry = products[p.id] = { product: p, chips: [], sel: {}, variant: null };
      // default selection = first available variant (else first)
      var def = null;
      for (var i = 0; i < p.variants.length; i++) {
        if (p.variants[i].available) { def = p.variants[i]; break; }
      }
      if (!def) def = p.variants[0];
      if (def) {
        (def.options || []).forEach(function (v, idx) { entry.sel[idx] = v; });
        entry.variant = def;
      }
    }
    if (entry.chips.indexOf(p.chip) === -1) entry.chips.push(p.chip);
    return entry;
  }

  function buildProducts() {
    products = {};
    var list = (catalog && catalog.products) || [];
    list.forEach(addProductEntry);
  }

  function optionValues(product, pos) {
    // distinct values at option index `pos`, in first-seen order
    var seen = {}, out = [];
    product.variants.forEach(function (v) {
      var val = (v.options || [])[pos];
      if (val != null && !seen[val]) { seen[val] = 1; out.push(val); }
    });
    return out;
  }

  function valueAvailable(product, pos, val) {
    return product.variants.some(function (v) {
      return (v.options || [])[pos] === val && v.available;
    });
  }

  // Does this product have a REAL choice to make (any option with >1 value)?
  // Used to decide whether Add opens the variant popup or adds straight away.
  function productHasOptions(product) {
    return (product.options || []).some(function (o, i) {
      return optionValues(product, i).length > 1;
    });
  }

  function resolveVariant(entry) {
    var p = entry.product;
    var match = p.variants.filter(function (v) {
      return (v.options || []).every(function (val, idx) {
        return entry.sel[idx] == null || entry.sel[idx] === val;
      });
    });
    // prefer an available match
    var avail = match.filter(function (v) { return v.available; });
    entry.variant = avail[0] || match[0] || p.variants[0] || null;
    return entry.variant;
  }

  function variantMeta(product, variant) {
    if (!variant) return '';
    if (variant.title && variant.title !== 'Default Title') return variant.title;
    return product.type || '';
  }

  // Hide junk meta values: blank, "Default Title", or numeric placeholder types
  // (e.g. "0", "7") that some products carry from imports.
  function cleanMeta(s) {
    s = String(s == null ? '' : s).trim();
    if (!s || /^\d+$/.test(s) || s.toLowerCase() === 'default title') return '';
    return s;
  }

  /* ---------- card rendering ---------- */
  function cardHTML(entry) {
    var p = entry.product;
    var v = entry.variant;
    // Image slider: use all product images (fallback to the single featured image).
    var imgs = (p.images && p.images.length) ? p.images : (p.image ? [p.image] : []);
    var img = '';
    if (imgs.length > 1) {
      var slides = imgs.map(function (src) {
        return '<img src="' + escapeAttr(src) + '" loading="lazy" alt="' + escapeAttr(p.title) + '">';
      }).join('');
      var dots = imgs.map(function (_s, di) {
        return '<span class="bb__thumb-dot' + (di === 0 ? ' on' : '') + '"></span>';
      }).join('');
      var cardArrows =
        '<button type="button" class="bb__thumb-arrow bb__thumb-arrow--prev" data-bb-arrow="prev" aria-label="Previous">' +
          '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M15 6l-6 6 6 6"/></svg>' +
        '</button>' +
        '<button type="button" class="bb__thumb-arrow bb__thumb-arrow--next" data-bb-arrow="next" aria-label="Next">' +
          '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg>' +
        '</button>';
      img = '<div class="bb__thumb-track" data-bb-track>' + slides + '</div>' + cardArrows +
            '<div class="bb__thumb-dots" data-bb-dots>' + dots + '</div>';
    } else if (imgs.length === 1) {
      img = '<img src="' + escapeAttr(imgs[0]) + '" width="300" height="300" loading="lazy" alt="' + escapeAttr(p.title) + '">';
    }
    var opts = '';
    var hasVisibleOpts = false;
    (p.options || []).forEach(function (o, i) {
      var vals = optionValues(p, i);
      if (vals.length <= 1) return; // hide single-value options (e.g. "Title")
      hasVisibleOpts = true;
      var sw = vals.map(function (val) {
        var sel = entry.sel[i] === val ? ' sel' : '';
        var avail = valueAvailable(p, i, val);
        var col = colorFor(val);
        var cls = 'bb__swatch' + (col ? ' has-dot' : '') + sel + (avail ? '' : ' soldout');
        var dot = col ? '<span class="bb__dot" style="background:' + col + '"></span>' : '';
        return '<button type="button" class="' + cls + '" data-bb-swatch="' + i + '" data-bb-value="' + escapeAttr(val) + '"' +
          ' title="' + escapeAttr(val) + '"' + (avail ? '' : ' aria-disabled="true"') + '>' + dot + escapeHtml(val) + '</button>';
      }).join('');
      opts += '<div class="bb__opt"><div class="bb__opt-name">' + escapeHtml(o.name) + '</div><div class="bb__swatches">' + sw + '</div></div>';
    });
    // When option swatches are shown, the variant title just duplicates them —
    // fall back to the product type (or nothing) so we don't print e.g. "White"
    // right above the COLOR: White / Brown swatches.
    var meta = cleanMeta(hasVisibleOpts ? (p.type || '') : variantMeta(p, v));
    // Cards stay clean (no inline swatches). Products WITH options open the
    // Quick View popup on Add to pick a variant; the `opts` string is unused here.
    return (
      '<div class="bb__badge">' + escapeHtml(str('inEdit')) + '</div>' +
      '<div class="bb__thumb' + (img ? '' : ' is-empty') + '" data-bb-thumb>' + img +
        '<button type="button" class="bb__qv-btn" data-bb-quick title="' + escapeAttr(str('quickView') || 'Quick view') + '" aria-label="' + escapeAttr(str('quickView') || 'Quick view') + '">' +
          '<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true"><path d="M1.5 12S5 5 12 5s10.5 7 10.5 7-3.5 7-10.5 7S1.5 12 1.5 12z"/><circle cx="12" cy="12" r="3"/></svg>' +
          '<span class="bb__qv-btn-t">' + escapeHtml(str('quickView') || 'Quick view') + '</span>' +
        '</button>' +
      '</div>' +
      '<div class="bb__card-body">' +
      '<div class="bb__card-info">' +
      '<h3>' + escapeHtml(p.title) + '</h3>' +
      (meta ? '<div class="meta">' + escapeHtml(meta) + '</div>' : '') +
      '<div class="bb__priceRow" data-bb-price></div>' +
      '</div>' +
      '<button type="button" class="bb__add" data-bb-add>' + escapeHtml(str('addToEdit')) + '</button>' +
      '</div>'
    );
  }

  // Hide per-item prices only in bundle mode. Advanced mode uses normal-priced
  // products, so prices always show there regardless of the bundle "hide prices"
  // setting.
  function hidePrice() {
    return !!(config && config.bundleHidePrice) && !(config && config.advanced);
  }

  function renderCardPrice(card, entry) {
    var v = entry.variant;
    var el = qs('[data-bb-price]', card);
    if (!el) return;
    if (hidePrice()) { el.innerHTML = ''; return; }
    if (!v) { el.innerHTML = ''; return; }
    var html = '<span class="bb__price">' + moneyEl(v.price);
    if (v.compare && v.compare > v.price) html += ' <s>' + moneyEl(v.compare) + '</s>';
    html += '</span>';
    el.innerHTML = html;
  }

  function refreshCardState(card, entry) {
    var v = entry.variant;
    // swatch selection + availability
    qsa('[data-bb-swatch]', card).forEach(function (sw) {
      var i = parseInt(sw.getAttribute('data-bb-swatch'), 10);
      var val = sw.getAttribute('data-bb-value');
      sw.classList.toggle('sel', entry.sel[i] === val);
    });
    renderCardPrice(card, entry);
    var add = qs('[data-bb-add]', card);
    var unavailable = !v || !v.available;
    // Is THIS product in the edit (by product id, so changing a swatch after
    // adding doesn't "unlock" the card)?
    var prodInEdit = productInEdit(entry.product.id);
    if (add) {
      var atMax = totals().count >= maxTotal();
      if (unavailable) {
        add.disabled = true;
        add.classList.remove('in', 'remove');
        add.textContent = str('soldOut');
      } else if (atMax) {
        // ALL tiers unlocked (top tier reached):
        //  - product already in the edit → "Remove" (free a slot to swap)
        //  - product not in the edit → blocked ("Bundle full")
        if (prodInEdit) {
          add.classList.add('in', 'remove');
          add.disabled = false;
          add.textContent = str('removeFromEdit') || 'Remove';
        } else {
          add.classList.remove('in', 'remove');
          add.disabled = true;
          add.textContent = str('bundleFull') || str('addToEdit');
        }
      } else {
        // Below max → always an ADD action (you can add the same product again).
        add.classList.remove('remove');
        add.disabled = false;
        if (prodInEdit) {
          add.classList.add('in');
          var lq = productPieces(entry.product.id);
          add.textContent = str('inEdit') + ' · ' + lq + ' · ' + str('addAnother');
        } else {
          add.classList.remove('in');
          add.textContent = str('addToEdit');
        }
      }
    }
    card.classList.toggle('in', prodInEdit);
  }

  function makeCard(entry, idx) {
    var card = document.createElement('article');
    card.className = 'bb__card';
    card.setAttribute('data-bb-card', entry.product.id);
    card.setAttribute('data-bb-chips', entry.chips.join(' '));
    card.innerHTML = cardHTML(entry);
    // Products with options open the Quick View popup on Add (vs adding blind).
    card.setAttribute('data-bb-hasopts', productHasOptions(entry.product) ? '1' : '');
    refreshCardState(card, entry);
    // staggered reveal
    var delay = Math.min(idx * 45, 600);
    setTimeout(function () { card.classList.add('reveal'); }, delay);
    return card;
  }

  function buildGrid() {
    var grid = qs('[data-bb-grid]');
    if (!grid) return;
    grid.innerHTML = '';
    Object.keys(products).forEach(function (pid, idx) {
      var card = makeCard(products[pid], idx);
      grid.appendChild(card);
      initCardSlider(card);
    });
  }

  /* ---------- full-collection loading ----------
     Liquid can only render the first `loadCap` (50) products of a collection, so
     the server-rendered catalog is just the seed. For every collection block
     whose true product count exceeds the cap we fetch the whole collection from
     /collections/{handle}/products.json (250 per request), convert each product
     to the catalog shape, and append the ones we don't already have. The grid
     pager (applyGrid) then paginates the full set. */
  var bbCatalogFetchSeq = 0;

  function normalizeAjaxProduct(p, chipId, chipLabel) {
    // Ask the CDN for grid-sized images (Liquid renders 600px versions).
    function sized(src, w) {
      if (!src) return src;
      return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
    }
    var images = (p.images || []).map(function (im) {
      return sized(typeof im === 'string' ? im : (im && im.src) || '', 600);
    }).filter(Boolean).slice(0, 5);
    var variants = (p.variants || []).map(function (v) {
      var opts = [v.option1, v.option2, v.option3].filter(function (x) { return x != null; });
      return {
        id: v.id,
        title: v.title,
        options: opts,
        price: Math.round(parseFloat(v.price) * 100) || 0,
        compare: v.compare_at_price ? (Math.round(parseFloat(v.compare_at_price) * 100) || 0) : 0,
        available: v.available !== false,
        image: sized((v.featured_image && v.featured_image.src) || null, 240)
      };
    });
    return {
      chip: chipId,
      id: p.id,
      title: p.title,
      type: p.product_type || chipLabel || '',
      url: '/products/' + p.handle,
      description: p.body_html || '',
      image: images[0] || null,
      images: images,
      available: variants.some(function (v) { return v.available; }),
      options: (p.options || []).map(function (o, i) {
        return { name: (o && o.name) || String(o), position: (o && o.position) || i + 1 };
      }),
      variants: variants
    };
  }

  // Merge fetched products into the live catalog: existing products just gain the
  // chip (so multi-collection products filter correctly), new ones get an entry
  // and a hidden card appended to the grid.
  function appendFetchedProducts(list) {
    var grid = qs('[data-bb-grid]');
    var added = false;
    list.forEach(function (p) {
      if (!p || !p.variants || !p.variants.length) return;
      var existing = products[p.id];
      var entry = addProductEntry(p);
      if (existing) {
        var card = qs('[data-bb-card="' + p.id + '"]');
        if (card) card.setAttribute('data-bb-chips', entry.chips.join(' '));
        return;
      }
      catalog.products.push(p);
      added = true;
      if (grid) {
        var newCard = makeCard(entry, 0);
        newCard.style.display = 'none'; // applyGrid decides visibility
        grid.appendChild(newCard);
        initCardSlider(newCard);
      }
    });
    if (added) applyGrid();
  }

  function fetchCollectionPage(handle, page) {
    return fetch('/collections/' + encodeURIComponent(handle) + '/products.json?limit=250&page=' + page, {
      headers: { Accept: 'application/json' }
    }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    });
  }

  function loadRemainingProducts() {
    var cols = (config && config.collections) || [];
    var cap = parseInt(config && config.loadCap, 10) || 50;
    var seq = ++bbCatalogFetchSeq;
    cols.forEach(function (c) {
      if (!c || !c.handle || !(c.count > cap)) return; // Liquid already loaded everything
      var pages = Math.min(Math.ceil(c.count / 250), 8); // safety cap: 2000 products
      var reqs = [];
      for (var pg = 1; pg <= pages; pg++) reqs.push(fetchCollectionPage(c.handle, pg));
      Promise.all(reqs).then(function (results) {
        if (seq !== bbCatalogFetchSeq) return; // section was re-initialised meanwhile
        var all = [];
        results.forEach(function (body) {
          ((body && body.products) || []).forEach(function (p) {
            all.push(normalizeAjaxProduct(p, c.chip, c.chipLabel));
          });
        });
        appendFetchedProducts(all);
      }).catch(function (e) {
        try { console.warn('[bundle-builder] could not load full collection "' + c.handle + '"', e); } catch (err) {}
      });
    });
  }


  // Card image slider: a native scroll-snap strip the shopper can scroll / swipe
  // through (desktop + touch). Mouse wheel scrolls horizontally through the
  // images, and the strip can be click-dragged. Dots reflect position and are
  // clickable.
  function initCardSlider(card) {
    var track = qs('[data-bb-track]', card);
    if (!track) return;
    var slides = qsa('img', track);
    if (slides.length < 2) return;
    var dots = qsa('[data-bb-dots] span', card);
    function setDots(i) { dots.forEach(function (d, di) { d.classList.toggle('on', di === i); }); }
    function current() { return track.clientWidth ? Math.round(track.scrollLeft / track.clientWidth) : 0; }
    function goTo(i) { track.scrollTo({ left: i * track.clientWidth, behavior: 'smooth' }); }

    track.addEventListener('scroll', function () { setDots(current()); }, { passive: true });
    dots.forEach(function (d, di) {
      d.addEventListener('click', function (e) { e.stopPropagation(); goTo(di); });
    });

    // optional prev/next arrows (used by the quick-view gallery)
    var prevBtn = qs('[data-bb-arrow="prev"]', card);
    var nextBtn = qs('[data-bb-arrow="next"]', card);
    // Wrap around both ways so the arrows loop infinitely (last → first, first → last).
    if (prevBtn) prevBtn.addEventListener('click', function (e) { e.stopPropagation(); goTo((current() - 1 + slides.length) % slides.length); });
    if (nextBtn) nextBtn.addEventListener('click', function (e) { e.stopPropagation(); goTo((current() + 1) % slides.length); });

    track.classList.add('bb__thumb-track--drag');

    // Horizontal trackpad/wheel gestures scroll through the images. A normal
    // VERTICAL mouse wheel is left alone so it keeps scrolling the PAGE — we must
    // never trap the page scroll just because the cursor is over a card.
    track.addEventListener('wheel', function (e) {
      // Ignore vertical-dominant gestures entirely (that's page scrolling).
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      var delta = e.deltaX;
      var atStart = track.scrollLeft <= 0;
      var atEnd = track.scrollLeft >= (track.scrollWidth - track.clientWidth - 1);
      // At the strip's edges let the browser handle it (so nothing feels stuck).
      if ((delta < 0 && atStart) || (delta > 0 && atEnd)) return;
      e.preventDefault();
      track.scrollLeft += delta;
    }, { passive: false });

    // Click-drag to scroll (desktop). A small movement threshold prevents a drag
    // from being read as a click on the card's swatches / add button.
    var down = false, startX = 0, startLeft = 0, moved = false;
    track.addEventListener('mousedown', function (e) {
      if (e.button !== 0) return;
      down = true; moved = false;
      startX = e.pageX; startLeft = track.scrollLeft;
      track.classList.add('is-grabbing');
    });
    document.addEventListener('mousemove', function (e) {
      if (!down) return;
      var dx = e.pageX - startX;
      if (Math.abs(dx) > 3) moved = true;
      track.scrollLeft = startLeft - dx;
      e.preventDefault();
    });
    document.addEventListener('mouseup', function () {
      if (!down) return;
      down = false;
      track.classList.remove('is-grabbing');
      if (moved) goTo(current()); // snap to the nearest image after a drag
    });
    // Swallow the click that follows a real drag so it doesn't hit the buttons.
    track.addEventListener('click', function (e) {
      if (moved) { e.stopPropagation(); e.preventDefault(); moved = false; }
    }, true);
  }

  /* ---------- quick view ---------- */
  // Interactive option swatches for the quick-view modal — clicking one changes
  // the entry's selected variant (kept in sync with the matching grid card).
  function qvOptionsHTML(entry) {
    var p = entry.product;
    var out = '';
    (p.options || []).forEach(function (o, i) {
      var vals = optionValues(p, i);
      if (vals.length <= 1) return;
      var sel = entry.sel[i];
      var sw = vals.map(function (val) {
        var col = colorFor(val);
        var avail = valueAvailable(p, i, val);
        var cls = 'bb__swatch' + (col ? ' has-dot' : '') + (sel === val ? ' sel' : '') + (avail ? '' : ' soldout');
        var dot = col ? '<span class="bb__dot" style="background:' + col + '"></span>' : '';
        return '<button type="button" class="' + cls + '" data-bb-qv-swatch="' + i + '" data-bb-value="' + escapeAttr(val) + '"' +
          ' title="' + escapeAttr(val) + '"' + (avail ? '' : ' aria-disabled="true"') + '>' + dot + escapeHtml(val) + '</button>';
      }).join('');
      out += '<div class="bb__opt"><div class="bb__opt-name">' + escapeHtml(o.name) + '</div><div class="bb__swatches">' + sw + '</div></div>';
    });
    return out;
  }

  function quickViewHTML(entry) {
    var p = entry.product;
    // gallery (same scroll-snap strip as the cards, but larger)
    var imgs = (p.images && p.images.length) ? p.images : (p.image ? [p.image] : []);
    var gallery = '';
    if (imgs.length > 1) {
      var slides = imgs.map(function (src) {
        return '<img src="' + escapeAttr(src) + '" loading="lazy" alt="' + escapeAttr(p.title) + '">';
      }).join('');
      var dots = imgs.map(function (_s, di) {
        return '<span class="bb__thumb-dot' + (di === 0 ? ' on' : '') + '"></span>';
      }).join('');
      var arrows =
        '<button type="button" class="bb__qv-arrow bb__qv-arrow--prev" data-bb-arrow="prev" aria-label="Previous">' +
          '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M15 6l-6 6 6 6"/></svg>' +
        '</button>' +
        '<button type="button" class="bb__qv-arrow bb__qv-arrow--next" data-bb-arrow="next" aria-label="Next">' +
          '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg>' +
        '</button>';
      gallery = '<div class="bb__thumb-track" data-bb-track>' + slides + '</div>' + arrows +
                '<div class="bb__thumb-dots" data-bb-dots>' + dots + '</div>';
    } else if (imgs.length === 1) {
      gallery = '<img src="' + escapeAttr(imgs[0]) + '" loading="lazy" alt="' + escapeAttr(p.title) + '">';
    }
    var eyebrow = cleanMeta(p.type || '');
    // Price is (re)filled by refreshQuickView so it tracks the selected variant.
    var priceHtml = hidePrice() ? '' : '<div class="bb__qv-price" data-bb-qv-price></div>';
    return (
      '<div class="bb__qv-gallery bb__thumb" data-bb-qv-gallery>' + gallery + '</div>' +
      '<div class="bb__qv-info">' +
        (eyebrow ? '<div class="bb__qv-eyebrow">' + escapeHtml(eyebrow) + '</div>' : '') +
        '<h3 class="bb__qv-h">' + escapeHtml(p.title) + '</h3>' +
        priceHtml +
        '<div class="bb__qv-opts">' + qvOptionsHTML(entry) + '</div>' +
        '<button type="button" class="bb__qv-add" data-bb-qv-add>' + escapeHtml(str('qvAdd') || 'Add to cart') + '</button>' +
        (p.description ? '<div class="bb__qv-desc rte">' + p.description + '</div>' : '') +
      '</div>'
    );
  }

  function openQuickView(entry) {
    var modal = qs('[data-bb-qv]');
    var body = qs('[data-bb-qv-body]', modal);
    if (!modal || !body || !entry) return;
    qvEntry = entry;
    body.innerHTML = quickViewHTML(entry);
    modal.classList.add('open');
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('bb-qv-lock');
    // wire the gallery slider (it lives in the modal, not a card)
    var gal = qs('[data-bb-qv-gallery]', modal);
    if (gal) initCardSlider(modal);
    // set the initial price + add-button state for the current variant
    refreshQuickView();
  }
  function closeQuickView() {
    var modal = qs('[data-bb-qv]');
    if (!modal) return;
    qvEntry = null;
    modal.classList.remove('open');
    modal.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('bb-qv-lock');
  }

  // Reflect the quick-view entry's current variant into the open modal: swatch
  // selection, price, and the add-to-edit button's label/disabled state. Mirrors
  // the grid card's refreshCardState() so the two surfaces behave the same.
  function refreshQuickView() {
    var modal = qs('[data-bb-qv]');
    if (!modal || !qvEntry) return;
    var entry = qvEntry;
    var v = entry.variant;
    // swatch selection
    qsa('[data-bb-qv-swatch]', modal).forEach(function (sw) {
      var i = parseInt(sw.getAttribute('data-bb-qv-swatch'), 10);
      var val = sw.getAttribute('data-bb-value');
      sw.classList.toggle('sel', entry.sel[i] === val);
    });
    // price
    var priceEl = qs('[data-bb-qv-price]', modal);
    if (priceEl) {
      if (hidePrice() || !v) {
        priceEl.innerHTML = '';
      } else {
        var html = '<span class="bb__price">' + moneyEl(v.price);
        if (v.compare && v.compare > v.price) html += ' <s>' + moneyEl(v.compare) + '</s>';
        html += '</span>';
        priceEl.innerHTML = html;
      }
    }
    // add-to-edit button: sold out → disabled "Sold out"; bundle full → disabled
    // "Bundle full"; otherwise enabled "Add to cart".
    var add = qs('[data-bb-qv-add]', modal);
    if (add) {
      var unavailable = !v || !v.available;
      var atMax = totals().count >= maxTotal();
      if (unavailable) {
        add.disabled = true;
        add.textContent = str('soldOut') || 'Sold out';
      } else if (atMax) {
        add.disabled = true;
        add.textContent = str('bundleFull') || 'Bundle full';
      } else {
        add.disabled = false;
        add.textContent = str('qvAdd') || 'Add to cart';
      }
    }

  }

  // Sale countdown bars: fixed end date OR per-visitor "evergreen" timer (stored
  // in localStorage so it survives reloads and resets when it runs out).
  function initCountdowns() {
    qsa('[data-bb-countdown]').forEach(function (el) {
      if (el._bbCd) return;
      el._bbCd = true;
      var mode = el.getAttribute('data-mode') || 'evergreen';
      var expire = el.getAttribute('data-expire') || 'keep';
      var deadline = null;
      if (mode === 'fixed') {
        var raw = (el.getAttribute('data-end') || '').trim();
        var parsed = raw ? Date.parse(raw.replace(' ', 'T')) : NaN;
        if (!isNaN(parsed)) deadline = parsed;
      }
      if (!deadline) {
        var hours = Number(el.getAttribute('data-hours')) || 24;
        var key = 'bb_cd_' + (el.getAttribute('data-id') || 'x');
        var saved = 0;
        try { saved = parseInt(localStorage.getItem(key), 10) || 0; } catch (e) {}
        var nowMs = Date.now();
        if (!saved || saved <= nowMs) {
          saved = nowMs + hours * 3600000;
          try { localStorage.setItem(key, String(saved)); } catch (e2) {}
        }
        deadline = saved;
      }
      var dEl = qs('[data-bb-cd="d"]', el), hEl = qs('[data-bb-cd="h"]', el),
          mEl = qs('[data-bb-cd="m"]', el), sEl = qs('[data-bb-cd="s"]', el);
      function pad(n) { return (n < 10 ? '0' : '') + n; }
      var timer;
      function tick() {
        var totalSec = Math.max(0, Math.floor((deadline - Date.now()) / 1000));
        var s = totalSec % 60;
        var m = Math.floor(totalSec / 60) % 60;
        var h, d;
        if (dEl) { d = Math.floor(totalSec / 86400); h = Math.floor(totalSec / 3600) % 24; dEl.textContent = pad(d); }
        else { h = Math.floor(totalSec / 3600); }
        if (hEl) hEl.textContent = pad(h);
        if (mEl) mEl.textContent = pad(m);
        if (sEl) sEl.textContent = pad(s);
        if (totalSec <= 0) {
          clearInterval(timer);
          if (expire === 'hide') el.style.display = 'none';
        }
      }
      tick();
      timer = setInterval(tick, 1000);
    });
  }

  var bbActiveChip = 'all';
  var bbGridPage = 1;

  function bbPageSize() {
    var n = config ? parseInt(config.pageSize, 10) : 0;
    return n && n > 0 ? n : 0; // 0 → no pagination (show every match)
  }

  // Show the cards matching the active chip, `page_size` at a time, then render the
  // pager. Called on load, on chip change, and on page change.
  function applyGrid() {
    var cards = qsa('[data-bb-card]');
    var ps = bbPageSize();
    var filtered = [];
    cards.forEach(function (card) {
      var chips = (card.getAttribute('data-bb-chips') || '').split(' ');
      if (bbActiveChip === 'all' || chips.indexOf(bbActiveChip) !== -1) filtered.push(card);
    });

    var total = filtered.length;
    var pages = ps > 0 ? Math.max(1, Math.ceil(total / ps)) : 1;
    if (bbGridPage > pages) bbGridPage = pages;
    if (bbGridPage < 1) bbGridPage = 1;
    var start = ps > 0 ? (bbGridPage - 1) * ps : 0;
    var end = ps > 0 ? start + ps : total;

    cards.forEach(function (card) { card.style.display = 'none'; });
    filtered.slice(start, end).forEach(function (card) { card.style.display = ''; });

    renderPager(pages);
  }

  // Chip entry point (kept the old name): switch chip, reset to page 1, re-render.
  function filterGrid(chip) {
    bbActiveChip = chip || 'all';
    bbGridPage = 1;
    applyGrid();
  }

  function renderPager(pages) {
    var wrap = qs('[data-bb-pagination]');
    if (!wrap) return;
    if (pages <= 1) { wrap.innerHTML = ''; wrap.hidden = true; return; }
    wrap.hidden = false;

    // Windowed page list: 1 … (cur-1) cur (cur+1) … last
    var nums = [];
    function push(n) { if (n >= 1 && n <= pages && nums.indexOf(n) === -1) nums.push(n); }
    push(1);
    push(bbGridPage - 1); push(bbGridPage); push(bbGridPage + 1);
    push(pages);
    nums.sort(function (a, b) { return a - b; });

    var html = '<button type="button" class="bb__page bb__page--prev" data-bb-page="' + (bbGridPage - 1) + '"' +
      (bbGridPage <= 1 ? ' disabled' : '') + ' aria-label="Previous">‹</button>';
    var prev = 0;
    nums.forEach(function (n) {
      if (prev && n - prev > 1) html += '<span class="bb__page-gap" aria-hidden="true">…</span>';
      html += '<button type="button" class="bb__page bb__page--num' + (n === bbGridPage ? ' is-active' : '') +
        '" data-bb-page="' + n + '"' + (n === bbGridPage ? ' aria-current="page"' : '') + '>' + n + '</button>';
      prev = n;
    });
    html += '<button type="button" class="bb__page bb__page--next" data-bb-page="' + (bbGridPage + 1) + '"' +
      (bbGridPage >= pages ? ' disabled' : '') + ' aria-label="Next">›</button>';
    wrap.innerHTML = html;
  }

  function goToPage(p) {
    if (isNaN(p)) return;
    bbGridPage = p;
    applyGrid();
    scrollToGrid();
  }

  /* ---------- totals / tiers ---------- */
  // Build a piece object for the edit list from a catalog entry + variant.
  function makePiece(entry, v) {
    return {
      pid: ++pieceSeq,
      variantId: v.id,
      productId: entry.product.id,
      title: entry.product.title,
      meta: cleanMeta(variantMeta(entry.product, v)),
      image: (v.image || entry.product.image) || '',
      price: v.price,
      compare: v.compare || 0
    };
  }
  // How many pieces of a given PRODUCT are in the edit (across its variants).
  function productPieces(pid) {
    return pieces.filter(function (p) { return p.productId === pid; }).length;
  }
  function productInEdit(pid) {
    return pieces.some(function (p) { return p.productId === pid; });
  }
  // Aggregated view (variantId → qty) derived from the piece list. Kept so the
  // pricing math, the Shopify cart payload and the saved draft can stay quantity
  // based even though the UI treats every piece individually.
  function lines() {
    var map = {}, order = [];
    pieces.forEach(function (p) {
      var k = String(p.variantId);
      if (!map[k]) {
        map[k] = { variantId: p.variantId, productId: p.productId, title: p.title, meta: p.meta, image: p.image, price: p.price, compare: p.compare, qty: 0 };
        order.push(k);
      }
      map[k].qty++;
    });
    return order.map(function (k) { return map[k]; });
  }
  function totals() {
    var count = 0, subtotal = 0, value = 0;
    lines().forEach(function (l) {
      count += l.qty;
      subtotal += l.qty * l.price;
      // "value" prefers the compare-at price (where the original price lives on a
      // 0.00 bundle variant) so we can still show a savings figure in bundle mode.
      var unit = (l.compare && l.compare > l.price) ? l.compare : l.price;
      value += l.qty * unit;
    });
    var tier = null, tierIdx = -1;
    tiers.forEach(function (t, i) { if (count >= t.req) { tier = t; tierIdx = i; } });
    var pct = tier ? Number(tier.pct) || 0 : 0;
    var bundle = config.pricingMode === 'bundle';
    var discount, total, was;
    if (bundle) {
      was = value || subtotal;
      if (tier && tier.bundlePrice > 0) {
        total = tier.bundlePrice;             // flat tier price
        discount = Math.max(0, was - total);  // savings vs. original value
      } else {
        total = was;                          // below the first tier — no flat price yet
        discount = 0;
      }
    } else {
      discount = Math.round(subtotal * pct / 100);
      total = subtotal - discount;
      was = subtotal;
    }
    return { count: count, subtotal: subtotal, value: value, was: was, tier: tier, tierIdx: tierIdx, pct: pct, discount: discount, total: total, bundle: bundle };
  }

  // Total pieces allowed = the top tier (Maximum rewards). Once reached, no more
  // pieces can be added. (There is no per-product cap — the same product can be
  // added multiple times until this total is hit.)
  function maxTotal() {
    var n = Number(config.maxReq);
    return (isFinite(n) && n > 0) ? n : Infinity;
  }

  // Highest tier's bundle price (the "any N for X" headline price). Tiers are
  // sorted ascending, so the last one with a price is the top tier.
  function topBundlePrice() {
    var p = 0;
    tiers.forEach(function (t) { if (Number(t.bundlePrice) > 0) p = Number(t.bundlePrice); });
    return p;
  }
  // Replace __PRICE__ in the offer banner + countdown copy with the top bundle
  // price, formatted in the customer's active currency.
  function applyPriceTokens() {
    var p = topBundlePrice();
    if (!p) return;
    var priceStr = moneyEl(p);
    qsa('.bb__offer-title, .bb__offer-body, .bb__sale-title, .bb__sale-sub').forEach(function (el) {
      if (el.innerHTML && el.innerHTML.indexOf('__PRICE__') !== -1) {
        el.innerHTML = el.innerHTML.replace(/__PRICE__/g, priceStr);
      }
    });
  }

  /* ---------- animations ---------- */
  function flyDot(fromEl) {
    if (!fromEl || (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)) return;
    var target = qs('[data-bb-total]');
    var bar = qs('[data-bb-bar-count]');
    if (bar && bar.offsetParent !== null) target = bar;
    if (!target || !target.getBoundingClientRect) return;
    var a = fromEl.getBoundingClientRect();
    var t = target.getBoundingClientRect();
    var d = document.createElement('div');
    d.className = 'bb-fly';
    d.style.left = (a.left + a.width / 2 - 7) + 'px';
    d.style.top = (a.top - 7) + 'px';
    document.body.appendChild(d);
    var dx = (t.left + t.width / 2) - (a.left + a.width / 2);
    var dy = (t.top + t.height / 2) - a.top;
    var anim = d.animate(
      [
        { transform: 'translate(0,0) scale(1)', opacity: 1 },
        { transform: 'translate(' + dx * 0.5 + 'px,' + (dy * 0.4 - 60) + 'px) scale(1.35)', opacity: 1, offset: 0.55 },
        { transform: 'translate(' + dx + 'px,' + dy + 'px) scale(.3)', opacity: 0.2 }
      ],
      { duration: 700, easing: 'cubic-bezier(.4,0,.2,1)' }
    );
    anim.onfinish = function () { d.remove(); };
  }

  function tween(el, from, to) {
    if (!el) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) { el.textContent = money(to); return; }
    var t0 = null, dur = 600;
    function step(ts) {
      if (t0 == null) t0 = ts;
      var k = Math.min(1, (ts - t0) / dur);
      var e = 1 - Math.pow(1 - k, 3);
      el.textContent = money(Math.round(from + (to - from) * e));
      if (k < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  var toastTimer;
  function toast(title, desc) {
    var el = qs('[data-bb-toast]');
    if (!el || (!title && !desc)) return;
    var tt = qs('[data-bb-toast-title]'), td = qs('[data-bb-toast-desc]');
    if (tt) tt.textContent = title || '';
    if (td) td.textContent = desc || '';
    el.classList.add('show');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { el.classList.remove('show'); }, 2800);
  }

  function pulse(el, cls) {
    if (!el) return;
    el.classList.remove(cls);
    void el.offsetWidth;
    el.classList.add(cls);
  }

  /* ---------- panel / lines ---------- */
  function lineHTML(l) {
    var img = l.image ? '<img src="' + escapeAttr(l.image) + '" alt="">' : '';
    return (
      '<div class="bb__line" data-bb-line="' + escapeAttr(l.variantId) + '">' +
      '<div class="lt">' + img + '</div>' +
      '<div class="li"><h4>' + escapeHtml(l.title) + '</h4>' +
      (l.meta ? '<div class="lm">' + escapeHtml(l.meta) + '</div>' : '') +
      '<div class="lp">' + moneyEl(l.price) + '</div></div>' +
      '<div class="bb__stepper">' +
      '<button type="button" data-bb-step="-1" data-bb-line-key="' + escapeAttr(l.variantId) + '" aria-label="Decrease">−</button>' +
      '<b>' + l.qty + '</b>' +
      '<button type="button" data-bb-step="1" data-bb-line-key="' + escapeAttr(l.variantId) + '" aria-label="Increase">+</button>' +
      '</div></div>'
    );
  }

  // Bundle-mode anchor line: a locked (non-deletable) row at the top of the panel
  // that shows the reached tier's flat price, so the shopper sees where the total
  // comes from (the pieces below ride along free).
  function anchorLineHTML(t) {
    var tier = t.tier;
    var title = (tier.anchorTitle && String(tier.anchorTitle).trim()) || tier.name || str('bundleLineTitle') || 'Bundle';
    var img = tier.anchorImage
      ? '<img src="' + escapeAttr(tier.anchorImage) + '" alt="">'
      : '';
    var sub = String(str('bundleLineLabel') || 'Bundle price');
    return (
      '<div class="bb__line bb__line--anchor">' +
      '<div class="lt">' + img + '</div>' +
      '<div class="li"><h4>' + escapeHtml(title) + '</h4>' +
      '<div class="lm">' + escapeHtml(sub) + '</div>' +
      '<div class="lp">' + moneyEl(t.total) + '</div></div>' +
      '<span class="bb__lock" title="' + escapeAttr(str('bundleLockedNote') || 'Included in your bundle') + '" aria-hidden="true">🔒</span>' +
      '</div>'
    );
  }

  // Order the ladder segments ascending by their target count and (re)compute each
  // segment's start from the previous segment's end. This makes the ladder correct
  // regardless of how the merchant ordered the tier blocks in the theme editor.
  function initSegments() {
    var wrap = qs('[data-bb-segs]');
    if (!wrap) return;
    var segs = qsa('[data-bb-seg]', wrap);
    if (!segs.length) return;
    segs.sort(function (a, b) {
      return (parseInt(a.getAttribute('data-to'), 10) || 0) - (parseInt(b.getAttribute('data-to'), 10) || 0);
    });
    var prev = 0;
    segs.forEach(function (seg) {
      var to = parseInt(seg.getAttribute('data-to'), 10) || 0;
      seg.setAttribute('data-from', prev);
      wrap.appendChild(seg); // reflow into ascending visual order
      prev = to;
      // Show the tier's bundle PRICE (in the active currency) on the segment
      // instead of the % chip, when a bundle price is configured for that tier.
      var tier = null;
      for (var i = 0; i < tiers.length; i++) {
        if (Number(tiers[i].req) === to) { tier = tiers[i]; break; }
      }
      var lbl = qs('[data-bb-seg-label]', seg) || qs('b', seg);
      if (lbl && tier && Number(tier.bundlePrice) > 0) {
        lbl.innerHTML = moneyEl(tier.bundlePrice);
      }
    });
  }

  function renderProgress(t) {
    // segmented reward ladder: each segment fills as the count moves from `from`→`to`.
    qsa('[data-bb-seg]').forEach(function (seg) {
      var from = parseInt(seg.getAttribute('data-from'), 10) || 0;
      var to = parseInt(seg.getAttribute('data-to'), 10) || 0;
      var span = (to - from) || 1;
      var prog = Math.max(0, Math.min(1, (t.count - from) / span));
      var bar = qs('.bb__rl-bar i', seg);
      if (bar) bar.style.width = (prog * 100) + '%';
      var reached = t.count >= to;
      var active = t.count > from && t.count < to;
      seg.classList.toggle('reached', reached);
      seg.classList.toggle('active', active);
      // pop the segment the moment it becomes fully reached
      if (reached && t.count > prevCount && to > prevCount) pulse(seg, 'pulse');
    });

    var tierEl = qs('[data-bb-prog-tier]');
    if (tierEl) {
      var name = t.count === 0 ? str('progressStart') : (t.tier ? t.tier.name : str('progressStart'));
      // keep the count span intact
      var countSpan = qs('[data-bb-prog-count]');
      tierEl.childNodes[0].nodeValue = name + ' ';
      if (countSpan) {
        var word = t.count === 1 ? str('pieceSingular') : str('piecePlural');
        countSpan.textContent = '· ' + t.count + ' ' + word;
      }
    }
    var hint = progressHint(t);
    qsa('[data-bb-prog-hint], [data-bb-prog-hint-wide]').forEach(function (el) { el.textContent = hint; });

    renderMobileProgress(t);
  }

  // Dedicated mobile progress: current tier + price, one bar toward the top tier,
  // and a "Add N more → next tier for price" line.
  function renderMobileProgress(t) {
    var wrap = qs('[data-bb-mprog]');
    if (!wrap) return;
    var tierEl = qs('[data-bb-mprog-tier]', wrap);
    var priceEl = qs('[data-bb-mprog-price]', wrap);
    var fillEl = qs('[data-bb-mprog-fill]', wrap);
    var nextEl = qs('[data-bb-mprog-next]', wrap);

    // Title: current tier name + count.
    if (tierEl) {
      var name = t.count === 0 ? str('progressStart') : (t.tier ? t.tier.name : str('progressStart'));
      var word = t.count === 1 ? str('pieceSingular') : str('piecePlural');
      tierEl.textContent = name + ' · ' + t.count + ' ' + word;
    }
    // Current reached price (blank until a tier is reached).
    if (priceEl) {
      priceEl.innerHTML = (t.tier && Number(t.tier.bundlePrice) > 0) ? moneyEl(t.tier.bundlePrice) : '';
    }
    // Bar = overall progress toward the top tier (Maximum rewards).
    if (fillEl) {
      var max = Number(config.maxReq) || 1;
      var pct = Math.max(0, Math.min(100, (t.count / max) * 100));
      fillEl.style.width = pct + '%';
    }
    // Next-reward line.
    if (nextEl) {
      var next = null;
      for (var i = 0; i < tiers.length; i++) {
        if (tiers[i].req > t.count) { next = tiers[i]; break; }
      }
      if (!next) {
        nextEl.textContent = str('unlockedAll');
      } else {
        var remaining = next.req - t.count;
        var nextName = next.nodeLabel || next.name || '';
        var nextPrice = Number(next.bundlePrice) > 0 ? moneyEl(next.bundlePrice) : '';
        nextEl.innerHTML = String(str('mobileNext') || 'Add __N__ more → __TIER__ for __PRICE__')
          .replace(/__N__/g, remaining)
          .replace(/__TIER__/g, escapeHtml(nextName))
          .replace(/__PRICE__/g, nextPrice);
      }
    }
  }

  // Mobile bottom-bar helper text (shown only when the edit has pieces but no
  // savings yet). Accepts __N__ = pieces remaining to the next reward.
  function barProgressText(t) {
    var next = null;
    for (var i = 0; i < tiers.length; i++) {
      if (tiers[i].req > t.count) { next = tiers[i]; break; }
    }
    if (!next) return str('unlockedAll');
    var remaining = Math.max(0, next.req - t.count);
    var tmpl = str('barProgress') || str('reserveNeedMore') || 'Add __N__ more';
    return String(tmpl).replace(/__N__/g, remaining);
  }

  function progressHint(t) {
    var maxReq = Number(config.maxReq) || 1;
    if (t.count >= maxReq) return str('unlockedAll');
    // next tier = smallest req greater than count
    var next = null;
    for (var i = 0; i < tiers.length; i++) {
      if (tiers[i].req > t.count) { next = tiers[i]; break; }
    }
    if (!next) return str('unlockedAll');
    var remaining = next.req - t.count;
    return String(next.hint || '').replace(/__N__/g, remaining);
  }

  // One thumbnail in the recap tray = ONE piece. `data-bb-piece` is the piece's
  // unique id (used by the ✕ to remove exactly this piece); `data-bb-tray-item`
  // keeps the variantId so tapping the thumbnail can open that product's quick
  // view.
  function trayItemHTML(p) {
    var img = p.image ? '<img src="' + escapeAttr(p.image) + '" alt="' + escapeAttr(p.title) + '">' : '';
    return (
      '<div class="bb__tray-item" data-bb-piece="' + escapeAttr(p.pid) + '" data-bb-tray-item="' + escapeAttr(p.variantId) + '" role="button" tabindex="0" title="' + escapeAttr(p.title) + '">' +
      img +
      '<button type="button" class="bb__tray-x" data-bb-piece-remove="' + escapeAttr(p.pid) + '" aria-label="Remove ' + escapeAttr(p.title) + '">&times;</button>' +
      '</div>'
    );
  }
  // Remove exactly ONE piece by its unique id — the tray ✕.
  function removePiece(pid) {
    if (pid == null || pid === '') return;
    var key = String(pid);
    for (var i = 0; i < pieces.length; i++) {
      if (String(pieces[i].pid) === key) { pieces.splice(i, 1); break; }
    }
    if (!pieces.length) clearSavedState();
    render();
  }

  // Competitor-style recap: a fixed bottom tray with the selected-piece
  // thumbnails, a count badge, a status message and the Reserve/Checkout CTA.
  function renderPanel(t) {
    var minReq = Number(str('reserveMin')) || 1;

    // Reserve / checkout CTA (shared hook: data-bb-reserve / reserve()).
    var rs = qs('[data-bb-reserve]');
    var rsLabel = qs('[data-bb-reserve-label]');
    if (rs && rsLabel) {
      var ready = t.count >= minReq;
      rs.disabled = !ready;
      rsLabel.innerHTML = ready
        ? (escapeHtml(str('reserveReady')) + ' · ' + moneyEl(t.total))
        : escapeHtml(str('reserveNeedMore').replace(/__N__/g, Math.max(0, minReq - t.count)));
    }

    // Count badge + progress ring (stroke fills to count/max; glows when full).
    var countEl = qs('[data-bb-bar-count]');
    if (countEl) countEl.textContent = t.count;
    var ring = qs('[data-bb-count-ring]');
    if (ring) {
      var mx = maxTotal();
      var pct = (isFinite(mx) && mx > 0) ? Math.min(100, Math.round(t.count / mx * 100)) : 0;
      ring.style.setProperty('--bb-count-pct', pct);
      ring.classList.toggle('full', t.count > 0 && pct >= 100);
      if (t.count > prevCount) pulse(ring, 'bump');
    }

    // Status line: nudge toward the next tier ("Add __N__ more → __TIER__ for
    // __PRICE__"); at the top tier show savings if any, else "all unlocked".
    var msgEl = qs('[data-bb-tray-msg]');
    if (msgEl) {
      if (t.count === 0) {
        msgEl.innerHTML = escapeHtml(str('emptyEdit'));
      } else {
        var next = null;
        for (var i = 0; i < tiers.length; i++) {
          if (tiers[i].req > t.count) { next = tiers[i]; break; }
        }
        if (next) {
          var remaining = next.req - t.count;
          var nextName = next.nodeLabel || next.name || '';
          var nextPrice = Number(next.bundlePrice) > 0 ? moneyEl(next.bundlePrice) : '';
          msgEl.innerHTML = String(str('mobileNext') || 'Add __N__ more → __TIER__ for __PRICE__')
            .replace(/__N__/g, remaining)
            .replace(/__TIER__/g, escapeHtml(nextName))
            .replace(/__PRICE__/g, nextPrice)
            .replace(/\s+→\s*$/, '').replace(/\s+for\s*$/, ''); // tidy if tokens were blank
        } else {
          // Top tier reached: show a plain message (no price / savings amount).
          msgEl.innerHTML = escapeHtml(str('unlockedAll'));
        }
      }
    }

    // Selected-piece thumbnails. Only rebuild when the set actually changed —
    // rebuilding on every render replays the entrance animation and re-creates
    // the ✕ buttons, which shifts click targets (a fast 2nd click could then hit
    // a different item and feel like it "removed all").
    var itemsEl = qs('[data-bb-tray-items]');
    if (itemsEl) {
      // Signature = the exact ordered list of piece ids. It only changes when a
      // piece is added or removed, so we don't needlessly rebuild (which would
      // replay the entrance animation and shift ✕ click targets).
      var sig = pieces.map(function (p) { return p.pid; }).join('|');
      if (itemsEl.getAttribute('data-bb-sig') !== sig) {
        // One thumbnail PER PIECE (competitor style) — adding the same product 3×
        // shows it 3 times, and each ✕ removes only its own piece.
        var html = '';
        pieces.forEach(function (p) { html += trayItemHTML(p); });
        itemsEl.innerHTML = html;
        itemsEl.setAttribute('data-bb-sig', sig);
      }
    }

    var tray = qs('[data-bb-tray]');
    if (tray) tray.classList.toggle('empty', t.count === 0);
    // Reserve space at the bottom of the PAGE so the fixed tray never covers the
    // footer. Padding is added to <body> and matches the tray's real height.
    syncTraySpace();
  }

  // Push the page up by the tray's height while it's visible (so the footer
  // clears it); remove the space when the tray is hidden/empty.
  function syncTraySpace() {
    try {
      var tray = qs('[data-bb-tray]');
      var empty = !tray || tray.classList.contains('empty');
      var h = (tray && !empty) ? tray.offsetHeight : 0;
      document.body.style.paddingBottom = h ? (h + 10) + 'px' : '';
    } catch (e) {}
  }

  function render() {
    var t = totals();
    renderProgress(t);
    renderPanel(t);
    // refresh card add-button states
    qsa('[data-bb-card]').forEach(function (card) {
      var entry = products[card.getAttribute('data-bb-card')];
      if (entry) refreshCardState(card, entry);
    });
    // keep the quick-view modal (if open) consistent with the current edit
    if (qvEntry) refreshQuickView();
    // (Tier-unlock toast intentionally disabled — the progress ladder + tray
    // message already communicate the reward; error toasts still fire elsewhere.)
    prevCount = t.count;
    prevTierIdx = t.tierIdx;
    saveState();
  }

  /* ---------- persistence (localStorage) ---------- */
  // The shopper's in-progress edit survives reloads / return visits. We store only
  // {variantId, qty} and re-hydrate against the live catalog so prices, titles and
  // availability are always current (and removed/sold-out items silently drop).
  function storageKey() {
    var id = (root && root.getAttribute('data-section-id')) || 'default';
    return 'bb_edit_' + id;
  }
  function saveState() {
    try {
      var ls = lines();
      if (!ls.length) { localStorage.removeItem(storageKey()); return; }
      var data = ls.map(function (l) { return { v: l.variantId, q: l.qty }; });
      localStorage.setItem(storageKey(), JSON.stringify(data));
    } catch (e) {}
  }
  function clearSavedState() {
    try { localStorage.removeItem(storageKey()); } catch (e) {}
  }
  function findVariant(variantId) {
    var vid = String(variantId);
    var keys = Object.keys(products);
    for (var i = 0; i < keys.length; i++) {
      var entry = products[keys[i]];
      var vars = entry.product.variants || [];
      for (var j = 0; j < vars.length; j++) {
        if (String(vars[j].id) === vid) return { entry: entry, variant: vars[j] };
      }
    }
    return null;
  }
  function loadSavedState() {
    var out = [];
    try {
      var raw = localStorage.getItem(storageKey());
      if (!raw) return out;
      var data = JSON.parse(raw);
      if (!Array.isArray(data)) return out;
      data.forEach(function (item) {
        // New format: item is a bare variantId (one piece each). Old format:
        // { v: variantId, q: qty } — expand it into `q` individual pieces.
        var vid = (item && typeof item === 'object') ? item.v : item;
        var qty = (item && typeof item === 'object') ? Math.max(0, parseInt(item.q, 10) || 0) : 1;
        if (!qty) return;
        var found = findVariant(vid);
        if (!found || !found.variant.available) return; // drop removed / sold-out
        var v = found.variant;
        for (var k = 0; k < qty; k++) out.push(makePiece(found.entry, v));
        // reflect the restored choice in the card's swatches
        (v.options || []).forEach(function (val, idx) { found.entry.sel[idx] = val; });
        found.entry.variant = v;
      });
    } catch (e) {}
    return out;
  }

  /* ---------- edit mutations ---------- */
  // Remove a product from the edit entirely (all its variant lines). Used by the
  // card's in-edit "Remove" toggle.
  function removeProduct(entry) {
    if (!entry) return;
    var pid = entry.product.id;
    pieces = pieces.filter(function (p) { return p.productId !== pid; });
    if (!pieces.length) clearSavedState();
    render();
  }

  function addEntry(entry, fromEl) {
    var v = resolveVariant(entry);
    if (!v || !v.available) return;
    // Only the bundle's TOTAL max (top tier) caps the edit — the same product can
    // be added multiple times until that total is reached. (Silent — no toast.)
    if (pieces.length >= maxTotal()) { return; }
    // Every add is a brand-new piece with its own id (no qty merging).
    pieces.push(makePiece(entry, v));
    flyDot(fromEl);
    render();
  }
  // Stepper (+/-) — operates on the FIRST/LAST piece of a variant. The panel's
  // stepper UI isn't currently rendered (the tray uses per-piece ✕), but keep
  // this working on the piece list for any caller.
  function step(variantId, delta) {
    if (delta > 0) {
      if (pieces.length >= maxTotal()) return;
      var found = findVariant(variantId);
      if (found) pieces.push(makePiece(found.entry, found.variant));
    } else {
      for (var i = pieces.length - 1; i >= 0; i--) {
        if (String(pieces[i].variantId) === String(variantId)) { pieces.splice(i, 1); break; }
      }
    }
    if (!pieces.length) clearSavedState();
    render();
  }

  /* ---------- commit to Shopify cart ---------- */
  function reserve() {
    var ls = lines();
    if (!ls.length) return;
    // Advanced mode: don't checkout directly — open the discount-code popup,
    // which validates a code against Shopify and reveals the real price first.
    if (config && config.advanced) { openDiscount(); return; }
    var rs = qs('[data-bb-reserve]');
    if (rs) rs.classList.add('loading');
    var t0 = totals();
    var items;
    if (t0.bundle && t0.tier && t0.tier.anchorId) {
      // Fixed-price bundle: one priced "anchor" line carries the flat tier price;
      // the chosen pieces ride along (their variants should be the 0.00 bundle
      // variants so they show as FREE at checkout — like the competitor).
      items = [{ id: t0.tier.anchorId, quantity: 1, properties: { '_bundle': t0.tier.name } }];
      ls.forEach(function (l) {
        items.push({ id: l.variantId, quantity: l.qty, properties: { '_bundle': t0.tier.name } });
      });
    } else {
      items = ls.map(function (l) { return { id: l.variantId, quantity: l.qty }; });
    }
    // Locale/market-aware: Shopify Markets prefixes routes (e.g. /en-sa/), so a bare /cart/add.js 404s.
    var addUrl = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'cart/add.js';
    fetch(addUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) {
        // Always read the body — Shopify returns a JSON error (status/message/
        // description) on failure (e.g. an invalid or sold-out variant id).
        return r.json().catch(function () { return {}; }).then(function (body) {
          if (!r.ok) {
            var msg = body && (body.description || body.message) ? (body.description || body.message) : ('HTTP ' + r.status);
            // Log the full payload so the merchant can see WHICH id failed.
            try { console.error('[bundle-builder] /cart/add failed:', msg, '\nItems sent:', items, '\nShopify said:', body); } catch (e) {}
            var err = new Error(msg); err.bbDescription = msg; throw err;
          }
          return body;
        });
      })
      .then(function () {
        try { document.dispatchEvent(new CustomEvent('cart:updated')); } catch (e) {}
        // The edit is now committed to the real cart — drop the saved draft so a
        // return visit doesn't restore an order the shopper already reserved.
        clearSavedState();
        var after = config.afterReserve || 'checkout';
        var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        // Per-tier discount code: apply the highest-reached tier's code, if any.
        var reachedTier = totals().tier;
        var code = (reachedTier && reachedTier.code) ? String(reachedTier.code).trim() : '';
        // /discount/CODE?redirect=… sets the cart's discount and carries it to the destination.
        function withCode(dest) {
          return code ? root + 'discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(dest) : null;
        }
        if (after === 'drawer') {
          if (rs) rs.classList.remove('loading');
          try { document.dispatchEvent(new CustomEvent('cart:open')); } catch (e2) {}
          try { document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: {} })); } catch (e3) {}
        } else if (after === 'cart') {
          window.location.href = withCode('/cart') || (root + 'cart');
        } else {
          // Go to checkout (default). /checkout is locale/market-safe via the cart cookie.
          window.location.href = withCode('/checkout') || '/checkout';
        }
      })
      .catch(function (err) {
        if (rs) rs.classList.remove('loading');
        var detail = (err && err.bbDescription) ? err.bbDescription : str('errorAdd');
        toast('', detail);
        var sb = qs('[data-bb-reserve-label]');
        if (sb) sb.textContent = str('errorAdd');
      });
  }

  /* ---------- advanced mode: discount-code popup ---------- */
  // Shopify has no storefront API to validate a code + compute its discount
  // without a cart, so we: (1) sync the cart to the edit, (2) apply the code via
  // /discount/{code}, (3) read /cart.js for the REAL totals Shopify will honour.
  var discCode = '';       // last successfully-applied code
  var discBusy = false;    // guard against double-submits

  function shopRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }
  // Copy the editable labels into the modal (called on (re)load).
  function fillDiscStrings() {
    var modal = qs('[data-bb-disc]');
    if (!modal) return;
    var set = function (sel, val) { var el = qs(sel, modal); if (el) el.textContent = val || ''; };
    set('[data-bb-disc-heading]', str('discHeading'));
    set('[data-bb-disc-sub]', str('discSub'));
    set('[data-bb-disc-old-label]', str('discOldLabel') || 'Original');
    set('[data-bb-disc-new-label]', str('discNewLabel') || 'Your price');
    var applyT = qs('.bb__disc-apply-t', modal); if (applyT) applyT.textContent = str('discApply') || 'Apply code';
    var co = qs('[data-bb-disc-checkout]', modal); if (co) co.textContent = str('discCheckout') || 'Checkout';
    var inp = qs('[data-bb-disc-input]', modal); if (inp) inp.placeholder = str('discPlaceholder') || 'Discount code';
  }
  // The chosen pieces, listed in the popup (aggregated by variant, with qty).
  function renderDiscItems() {
    var modal = qs('[data-bb-disc]');
    var wrap = modal && qs('[data-bb-disc-items]', modal);
    if (!wrap) return;
    wrap.innerHTML = lines().map(function (l) {
      var img = l.image ? '<img src="' + escapeAttr(l.image) + '" alt="">' : '';
      return '<div class="bb__disc-item">' +
        '<div class="bb__disc-thumb">' + img + (l.qty > 1 ? '<span class="bb__disc-qb">' + l.qty + '</span>' : '') + '</div>' +
        '<div class="bb__disc-meta"><h5>' + escapeHtml(l.title) + '</h5>' +
        (l.meta ? '<span>' + escapeHtml(l.meta) + '</span>' : '') + '</div>' +
        '<div class="bb__disc-lp">' + moneyEl(l.qty * l.price) + '</div>' +
        '</div>';
    }).join('');
    // Seed the "original" figure from the client subtotal (before any code).
    var oldEl = qs('[data-bb-disc-old]', modal); if (oldEl) oldEl.innerHTML = moneyEl(totals().subtotal);
  }
  function openDiscount() {
    var modal = qs('[data-bb-disc]');
    if (!modal || !lines().length) return;
    // reset UI
    var err = qs('[data-bb-disc-error]', modal); if (err) { err.hidden = true; err.textContent = ''; }
    var sum = qs('[data-bb-disc-summary]', modal); if (sum) sum.hidden = true;
    var co = qs('[data-bb-disc-checkout]', modal); if (co) co.disabled = true;
    var inp = qs('[data-bb-disc-input]', modal); if (inp) inp.value = '';
    discCode = '';
    renderDiscItems();
    modal.classList.add('open');
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('bb-qv-lock');
    if (inp) setTimeout(function () { try { inp.focus(); } catch (e) {} }, 60);
  }
  function closeDiscount() {
    var modal = qs('[data-bb-disc]');
    if (!modal) return;
    modal.classList.remove('open');
    modal.setAttribute('aria-hidden', 'true');
    var qv = qs('[data-bb-qv]');
    if (!qv || !qv.classList.contains('open')) document.documentElement.classList.remove('bb-qv-lock');
  }
  // Is the code actually applied to the cart (covers $0-discount codes like free
  // shipping, which still count as "valid")?
  function discountApplied(cart, code) {
    var c = String(code).toLowerCase();
    var apps = (cart && cart.cart_level_discount_applications) || [];
    if (apps.some(function (a) { return a && String(a.title || a.code || '').toLowerCase() === c; })) return true;
    var codes = (cart && cart.discount_codes) || [];
    return codes.some(function (d) { return d && String(d.code || '').toLowerCase() === c && d.applicable !== false; });
  }
  function showDiscSummary(cart, code) {
    var modal = qs('[data-bb-disc]');
    var old = Number(cart.items_subtotal_price); if (!isFinite(old)) old = totals().subtotal;
    var save = Number(cart.total_discount) || 0;
    var now = Number(cart.total_price); if (!isFinite(now)) now = Math.max(0, old - save);
    var oldEl = qs('[data-bb-disc-old]', modal); if (oldEl) oldEl.innerHTML = moneyEl(old);
    var newEl = qs('[data-bb-disc-new]', modal); if (newEl) newEl.innerHTML = moneyEl(now);
    var codeEl = qs('[data-bb-disc-code]', modal); if (codeEl) codeEl.textContent = code;
    var saveEl = qs('[data-bb-disc-save]', modal);
    if (saveEl) {
      if (save > 0) { saveEl.hidden = false; saveEl.innerHTML = String(str('discSave') || 'You save __AMOUNT__').replace('__AMOUNT__', moneyEl(save)); }
      else saveEl.hidden = true;
    }
    var sum = qs('[data-bb-disc-summary]', modal); if (sum) sum.hidden = false;
    var co = qs('[data-bb-disc-checkout]', modal); if (co) co.disabled = false;
  }
  function applyDiscount() {
    var modal = qs('[data-bb-disc]');
    if (!modal || discBusy) return;
    var inp = qs('[data-bb-disc-input]', modal);
    var code = inp ? String(inp.value || '').trim() : '';
    var err = qs('[data-bb-disc-error]', modal);
    var apply = qs('[data-bb-disc-apply]', modal);
    function fail(msg) { if (err) { err.hidden = false; err.textContent = msg; } var sum = qs('[data-bb-disc-summary]', modal); if (sum) sum.hidden = true; var co = qs('[data-bb-disc-checkout]', modal); if (co) co.disabled = true; }
    var ls = lines();
    if (!ls.length) return;
    if (!code) { fail(str('discEnter') || 'Please enter a discount code.'); return; }
    discBusy = true;
    if (apply) apply.classList.add('loading');
    if (err) { err.hidden = true; err.textContent = ''; }
    var root = shopRoot();
    var items = ls.map(function (l) { return { id: l.variantId, quantity: l.qty }; });
    var jsonH = { 'Content-Type': 'application/json', Accept: 'application/json' };
    // 1) make the cart EXACTLY the edit (clear → add) so the discount math is right
    fetch(root + 'cart/clear.js', { method: 'POST', credentials: 'same-origin', headers: jsonH })
      .then(function () { return fetch(root + 'cart/add.js', { method: 'POST', credentials: 'same-origin', headers: jsonH, body: JSON.stringify({ items: items }) }); })
      .then(function (r) { return r.json().catch(function () { return {}; }).then(function (b) { if (!r.ok) throw new Error((b && (b.description || b.message)) || 'add failed'); return b; }); })
      // 2) apply the code to the cart session (fetch follows the redirect)
      .then(function () { return fetch(root + 'discount/' + encodeURIComponent(code), { credentials: 'same-origin' }); })
      // 3) read the real, Shopify-computed totals
      .then(function () { return fetch(root + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } }); })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        discBusy = false;
        if (apply) apply.classList.remove('loading');
        var save = Number(cart.total_discount) || 0;
        if (save > 0 || discountApplied(cart, code)) {
          discCode = code;
          showDiscSummary(cart, code);
        } else {
          fail(str('discInvalid') || 'That code isn’t valid for these items.');
        }
      })
      .catch(function () {
        discBusy = false;
        if (apply) apply.classList.remove('loading');
        fail(str('errorAdd') || 'Something went wrong. Please try again.');
      });
  }
  // Go to checkout with the applied code (re-asserted via /discount/CODE?redirect).
  function discountCheckout() {
    var root = shopRoot();
    var dest = root + 'checkout';
    window.location.href = discCode
      ? root + 'discount/' + encodeURIComponent(discCode) + '?redirect=' + encodeURIComponent(dest)
      : dest;
  }

  /* ---------- scroll to the "Begin your edit" ladder (CTA buttons) ---------- */
  function scrollToGrid() {
    // Prefer the progress ladder ("Begin your edit"); fall back to the grid.
    var prog = qs('[data-bb-progress]');
    var toProgress = prog && prog.offsetParent !== null;
    var target = toProgress ? prog : (qs('[data-bb-grid]') || qs('.bb__toolbar') || qs('.bb__main'));
    if (!target || !target.getBoundingClientRect) return;
    // Landing on the ladder itself: small offset. Landing on the grid: clear the sticky ladder.
    var offset = toProgress ? 8 : ((prog && prog.offsetParent !== null ? prog.offsetHeight : 0) + 16);
    var y = (window.pageYOffset || document.documentElement.scrollTop) + target.getBoundingClientRect().top - offset;
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: Math.max(0, y), behavior: reduce ? 'auto' : 'smooth' });
  }

  /* ---------- bottom-sheet open/close (mobile) ---------- */
  function openSheet() {
    var w = qs('[data-bb-panel-wrap]'), s = qs('[data-bb-scrim]');
    if (w) w.classList.add('open');
    if (s) s.classList.add('open');
  }
  function closeSheet() {
    var w = qs('[data-bb-panel-wrap]'), s = qs('[data-bb-scrim]');
    if (w) w.classList.remove('open');
    if (s) s.classList.remove('open');
  }

  /* ---------- soft-exit / navigation-warning modal ---------- */
  var exitPendingHref = null;
  var exitShown = false;

  function exitCfg() { return (config && config.exitIntent) || {}; }
  function exitSessionKey() {
    var id = (root && root.getAttribute('data-section-id')) || 'default';
    return 'bb_exit_shown_' + id;
  }
  function exitAlreadyShown() {
    if (!exitCfg().oncePerSession) return false;
    if (exitShown) return true;
    try { return sessionStorage.getItem(exitSessionKey()) === '1'; } catch (e) { return false; }
  }
  function markExitShown() {
    // Only remember it when "once per visit" is on — otherwise it re-shows freely.
    if (!exitCfg().oncePerSession) return;
    exitShown = true;
    try { sessionStorage.setItem(exitSessionKey(), '1'); } catch (e) {}
  }
  // Populate the modal copy from the editable strings (safe to call on re-render).
  function fillExitStrings() {
    var h = qs('[data-bb-exit-heading]'); if (h) h.textContent = str('exitHeading') || '';
    var b = qs('[data-bb-exit-body]'); if (b) b.textContent = str('exitBody') || '';
    var s = qs('[data-bb-exit-stay-label]'); if (s) s.textContent = str('exitStay') || 'Build My Set';
    var l = qs('[data-bb-exit-leave-label]'); if (l) l.textContent = str('exitLeave') || 'Leave anyway';
  }
  // Should we still trap this exit? (enabled, not yet shown, and — optionally —
  // only once the shopper has started an edit.)
  function exitArmed() {
    var c = exitCfg();
    if (!c.enabled) return false;
    if (exitAlreadyShown()) return false;
    if (c.onlyWithItems && !lines().length) return false;
    return true;
  }
  function openExit(href) {
    var modal = qs('[data-bb-exit]');
    if (!modal) return false;
    exitPendingHref = href || null;
    modal.classList.add('open');
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('bb-qv-lock');
    markExitShown();
    return true;
  }
  function closeExit() {
    var modal = qs('[data-bb-exit]');
    if (!modal) return;
    modal.classList.remove('open');
    modal.setAttribute('aria-hidden', 'true');
    // Don't release the scroll lock if the quick-view is still open underneath.
    var qv = qs('[data-bb-qv]');
    if (!qv || !qv.classList.contains('open')) document.documentElement.classList.remove('bb-qv-lock');
    exitPendingHref = null;
  }
  // "Leave anyway" → follow the link the shopper originally clicked (if any).
  function exitLeaveNow() {
    var href = exitPendingHref;
    closeExit();
    if (href) window.location.href = href;
  }
  // Global listeners (bound once): intercept off-page link clicks + desktop
  // exit-intent. These live outside #NuceryBundleBuilder (header menu, logo…),
  // so they can't ride the section's delegated handler.
  function armExitIntent() {
    // Global (cross-closure) guard — on a hot-reload dev theme each rebuild would
    // otherwise attach ANOTHER exit interceptor, and older ones (without the cart
    // guard below) stay bound to document and keep firing. Bind exactly once.
    if (armExitIntent._bound || window.__nuceryBBExitBound) return;
    armExitIntent._bound = true;
    window.__nuceryBBExitBound = true;

    document.addEventListener('click', function (e) {
      var c = exitCfg();
      if (!c.enabled || !c.interceptLinks || !exitArmed()) return;
      var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
      if (!a) return;
      if (a.closest('#NuceryBundleBuilder')) return;       // our own controls
      if (a.target && a.target !== '' && a.target !== '_self') return; // new tab/frame
      if (a.hasAttribute('download')) return;
      if (e.defaultPrevented) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;   // open-in-new-tab intent
      var href = a.getAttribute('href') || '';
      if (!href || href.charAt(0) === '#') return;
      if (/^(mailto:|tel:|sms:|javascript:)/i.test(href)) return;
      // The header cart icon / drawer toggle just OPENS the cart drawer — it is
      // NOT a real navigation, so never trap it. Catch it broadly, independent of
      // the exact href or whether the theme's handler preventDefaults in time:
      //  (a) the href points anywhere at /cart,
      //  (b) the anchor OR any ancestor looks cart/drawer related (class, id,
      //      aria-controls, data-*), which covers Impulse's cart/drawer markup.
      if (/\/cart(\/|\?|#|$)/i.test(href) || /(^|\/)cart\/?$/.test((href.split('?')[0] || ''))) return;
      var ariaCtl = (a.getAttribute('aria-controls') || '') + ' ' + (a.getAttribute('aria-label') || '') + ' ' + (a.getAttribute('data-action') || '');
      if (/cart|drawer|basket|bag/i.test(ariaCtl)) return;
      var toggleSel = '[href*="/cart"], [data-drawer-toggle], [data-cart-drawer], [data-cart-toggle],'
        + ' [data-nucery-cart-open], .js-nucery-cart-open, .site-header__icon--cart, .nucery-header__cart,'
        + ' .nucery-cart-drawer, [data-nucery-cart-backdrop], [data-nucery-cart-close],'
        + ' [class*="cart" i], [id*="cart" i], [class*="drawer" i], [id*="drawer" i],'
        + ' [aria-controls*="cart" i], [aria-controls*="drawer" i], .js-drawer-open-cart, .js-drawer-open-nav';
      // Any link INSIDE the open cart drawer (category tiles, "Shop all", the
      // close button) is an in-drawer navigation — let it close the drawer &
      // navigate normally, never trap it as a page exit.
      if (a.closest && a.closest(toggleSel)) return;
      var url;
      try { url = new URL(a.href, window.location.href); } catch (err) { return; }
      // Same-origin link to THIS same page (only the hash/nothing changes) → not
      // actually leaving. External sites and other internal pages both trap.
      var sameOrigin = url.origin === window.location.origin;
      if (sameOrigin && url.pathname === window.location.pathname && url.search === window.location.search) return;
      // Belt-and-suspenders: any same-origin URL whose path is the cart is a
      // drawer open, not a page exit.
      if (sameOrigin && /(^|\/)cart\/?$/.test(url.pathname)) return;
      e.preventDefault();
      e.stopPropagation();
      openExit(url.href);
    }, false); // BUBBLE phase (not capture): any theme handler that intercepts a
    // link first — cart drawer toggle, AJAX cart, quick-add — calls preventDefault
    // before this runs, so the `e.defaultPrevented` check above skips it. Only a
    // real off-page navigation (nothing preventing default) reaches openExit().
  }

  // Safety net for the cart/drawer case. A stale exit interceptor left bound by an
  // earlier hot-reload build (no cart guard) can't be unbound from here, so instead
  // we WATCH the exit modal: if it opens within a moment of a cart/drawer toggle
  // click, we immediately cancel it. This works no matter which interceptor (old,
  // new, or a theme script) opened the modal, because it acts on the shared DOM.
  function armCartExitSafety() {
    if (window.__nuceryBBCartSafe) return;
    window.__nuceryBBCartSafe = true;
    var lastCartClickAt = 0;
    document.addEventListener('click', function (e) {
      var el = e.target && e.target.closest
        ? e.target.closest('a, button, [role="button"], [data-nucery-cart-open], [data-drawer-toggle], [data-cart-drawer], [data-cart-toggle]')
        : null;
      if (!el) return;
      var hay = [
        (el.getAttribute && el.getAttribute('href')) || '',
        el.className || '',
        el.id || '',
        (el.getAttribute && el.getAttribute('aria-controls')) || '',
        (el.getAttribute && el.getAttribute('aria-label')) || ''
      ].join(' ').toLowerCase();
      if (/cart|drawer|basket|bag/.test(hay) ||
          (el.matches && el.matches('[data-nucery-cart-open], [data-drawer-toggle], [data-cart-drawer], [data-cart-toggle], .js-nucery-cart-open, .js-drawer-open-cart, .js-drawer-open-nav'))) {
        lastCartClickAt = Date.now();
      }
    }, true);
    var exitEl = qs('[data-bb-exit]');
    if (exitEl && window.MutationObserver) {
      new MutationObserver(function () {
        if (exitEl.classList.contains('open') && (Date.now() - lastCartClickAt) < 900) {
          // Opened right after a cart/drawer click → cancel it.
          exitEl.classList.remove('open');
          exitEl.setAttribute('aria-hidden', 'true');
          exitPendingHref = null;
          var qv = qs('[data-bb-qv]');
          if (!qv || !qv.classList.contains('open')) document.documentElement.classList.remove('bb-qv-lock');
        }
      }).observe(exitEl, { attributes: true, attributeFilter: ['class'] });
    }
  }

  /* ---------- events (delegated) ---------- */
  function bind() {
    // Global (cross-closure) guard: on a hot-reload dev theme the previous build's
    // document listeners stay attached after the file is swapped, so without this
    // a new build would ADD a second set — two reserve() handlers firing (one
    // opening the popup, an older one redirecting to checkout). Bind exactly once
    // per page session.
    if (bind._bound || window.__nuceryBBBound) return;
    bind._bound = true;
    window.__nuceryBBBound = true;

    // Advanced mode: intercept the tray Checkout button in the CAPTURE phase
    // (runs before any bubble-phase listener) and stopImmediatePropagation, so a
    // stale handler left over from a hot-reload build — or a theme script — can't
    // also fire and redirect to checkout while we open the discount popup.
    document.addEventListener('click', function (e) {
      if (!config || !config.advanced) return;
      var btn = e.target && e.target.closest ? e.target.closest('[data-bb-reserve]') : null;
      if (!btn) return;
      var rs = qs('[data-bb-reserve]');
      if (rs && rs.disabled) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      openDiscount();
    }, true);

    document.addEventListener('click', function (e) {
      var t = e.target;
      if (!t.closest) return;
      if (!t.closest('#NuceryBundleBuilder')) {
        // scrim/bar live inside the section too, so this also covers them
        return;
      }
      // Soft-exit modal (leave must be checked before stay: distinct hooks).
      if (t.closest('[data-bb-exit-leave]')) { exitLeaveNow(); return; }
      if (t.closest('[data-bb-exit-stay]')) { closeExit(); return; }
      // Advanced-mode discount modal.
      if (t.closest('[data-bb-disc-close]')) { closeDiscount(); return; }
      if (t.closest('[data-bb-disc-apply]')) { applyDiscount(); return; }
      if (t.closest('[data-bb-disc-checkout]')) { var dco = qs('[data-bb-disc-checkout]'); if (dco && !dco.disabled) discountCheckout(); return; }
      if (t.closest('[data-bb-scroll]')) { scrollToGrid(); return; }
      var chip = t.closest('[data-bb-chip]');
      if (chip) {
        qsa('[data-bb-chip]').forEach(function (c) { c.classList.remove('active'); });
        chip.classList.add('active');
        filterGrid(chip.getAttribute('data-bb-chip'));
        return;
      }
      var pageBtn = t.closest('[data-bb-page]');
      if (pageBtn) {
        if (pageBtn.disabled) return;
        goToPage(parseInt(pageBtn.getAttribute('data-bb-page'), 10));
        return;
      }
      var sw = t.closest('[data-bb-swatch]');
      if (sw) {
        if (sw.getAttribute('aria-disabled') === 'true') return;
        var card = sw.closest('[data-bb-card]');
        var entry = card && products[card.getAttribute('data-bb-card')];
        if (!entry) return;
        entry.sel[parseInt(sw.getAttribute('data-bb-swatch'), 10)] = sw.getAttribute('data-bb-value');
        resolveVariant(entry);
        refreshCardState(card, entry);
        return;
      }
      // Quick-view swatch: change the modal entry's variant (also updates the
      // matching grid card, since they share the same entry object).
      var qsw = t.closest('[data-bb-qv-swatch]');
      if (qsw) {
        if (qsw.getAttribute('aria-disabled') === 'true' || !qvEntry) return;
        qvEntry.sel[parseInt(qsw.getAttribute('data-bb-qv-swatch'), 10)] = qsw.getAttribute('data-bb-value');
        resolveVariant(qvEntry);
        refreshQuickView();
        var qcard = qs('[data-bb-card="' + qvEntry.product.id + '"]');
        if (qcard) refreshCardState(qcard, qvEntry);
        return;
      }
      // Quick-view "Add to cart" → add the selected variant to the edit, then
      // close the modal so the shopper returns to the grid.
      var qvAdd = t.closest('[data-bb-qv-add]');
      if (qvAdd) {
        if (qvAdd.disabled || !qvEntry) return;
        addEntry(qvEntry, qvAdd);
        closeQuickView();
        return;
      }
      if (t.closest('[data-bb-qv-close]')) { closeQuickView(); return; }
      var quick = t.closest('[data-bb-quick]');
      if (quick) {
        var qc = quick.closest('[data-bb-card]');
        var qe = qc && products[qc.getAttribute('data-bb-card')];
        if (qe) openQuickView(qe);
        return;
      }
      // Mobile: tapping the card image opens quick view (the icon button is hidden).
      var thumb = t.closest('[data-bb-thumb]');
      if (thumb && !t.closest('[data-bb-dots]') && window.matchMedia && window.matchMedia('(max-width: 999px)').matches) {
        var tc = thumb.closest('[data-bb-card]');
        var te = tc && products[tc.getAttribute('data-bb-card')];
        if (te) { openQuickView(te); return; }
      }
      var add = t.closest('[data-bb-add]');
      if (add) {
        if (add.disabled) return;
        var ac = add.closest('[data-bb-card]');
        var ae = ac && products[ac.getAttribute('data-bb-card')];
        if (!ae) return;
        // At the top tier (all rewards unlocked) the in-edit button is "Remove".
        if (pieces.length >= maxTotal() && productInEdit(ae.product.id)) { removeProduct(ae); return; }
        // Product with variant options → open the Quick View popup to pick a
        // variant (modal on desktop, bottom sheet on mobile). No options → add now.
        if (ac.getAttribute('data-bb-hasopts')) { openQuickView(ae); return; }
        addEntry(ae, add);
        return;
      }
      var trayRm = t.closest('[data-bb-piece-remove]');
      if (trayRm) { e.preventDefault(); e.stopPropagation(); removePiece(trayRm.getAttribute('data-bb-piece-remove')); return; }
      // Tapping a tray thumbnail (not the ✕) opens that product's Quick View.
      var trayItem = t.closest('[data-bb-tray-item]');
      if (trayItem) {
        var found = findVariant(trayItem.getAttribute('data-bb-tray-item'));
        if (found) openQuickView(found.entry);
        return;
      }
      var stepBtn = t.closest('[data-bb-step]');
      if (stepBtn) {
        step(stepBtn.getAttribute('data-bb-line-key'), parseInt(stepBtn.getAttribute('data-bb-step'), 10));
        return;
      }
      if (t.closest('[data-bb-reserve]')) {
        if (!qs('[data-bb-reserve]').disabled) reserve();
        return;
      }
      if (t.closest('[data-bb-close]') || t.closest('[data-bb-scrim]')) { closeSheet(); return; }
      if (t.closest('[data-bb-bar]')) { openSheet(); return; }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { closeSheet(); closeQuickView(); closeExit(); closeDiscount(); }
      // Enter inside the discount input applies the code.
      if (e.key === 'Enter' && e.target && e.target.closest && e.target.closest('[data-bb-disc-input]')) {
        e.preventDefault();
        applyDiscount();
        return;
      }
      if ((e.key === 'Enter' || e.key === ' ') && e.target && e.target.closest && e.target.closest('[data-bb-bar]')) {
        e.preventDefault();
        openSheet();
      }
    });

    window.addEventListener('scroll', function () {
      var prog = qs('[data-bb-progress]');
      if (prog) prog.classList.toggle('float', window.scrollY > 120);
    });

    // The tray height changes across breakpoints (it wraps on mobile) — keep the
    // reserved bottom space in sync so the footer always clears it.
    window.addEventListener('resize', syncTraySpace);
  }

  /* ---------- init ---------- */
  function syncFromConfig() {
    config = readJSON('script[data-bb-config]') || { strings: {} };
    config.strings = config.strings || {};
    catalog = readJSON('script[data-bb-catalog]') || { products: [] };
    tiers = (config.tiers || []).slice().sort(function (a, b) { return a.req - b.req; });
    prevCount = 0;
    prevTierIdx = -1;
    prevTotal = 0;
    buildProducts();
    // Re-hydrate the shopper's saved edit (needs the catalog built first).
    pieceSeq = 0;
    pieces = loadSavedState();
    buildGrid();
    applyGrid();
    // Pull in the products Liquid couldn't render (collections larger than 50).
    loadRemainingProducts();
    initSegments();
    render();
    initCountdowns();
    applyPriceTokens();
    fillExitStrings();
    fillDiscStrings();
    armExitIntent();
    armCartExitSafety();
  }

  function init() {
    // Idempotent: multiple boot() triggers (below) may call this; only the first
    // that finds the section in the DOM actually initialises.
    if (window.__nuceryBBInit) return;
    if (!ensureRoot()) return; // section not in the DOM yet — a later boot() retries
    // Proof-of-EXECUTION marker (distinct from __nuceryBBVersion, which is set at
    // parse time before the single-instance guard). If this is set, the piece
    // model actually ran and rendered.
    window.__nuceryBBInit = 'advanced-2026-07-21a';
    try { console.log('[bundle-builder] init', window.__nuceryBBInit); } catch (e) {}
    syncFromConfig();
    bind();
    document.addEventListener('shopify:section:load', onSectionLoad);
    document.addEventListener('shopify:section:select', onSectionLoad);
  }

  function onSectionLoad(e) {
    var sec = e && e.target;
    if (!sec || typeof sec.querySelector !== 'function') return;
    if (!sec.querySelector('#NuceryBundleBuilder') && sec.id !== 'NuceryBundleBuilder') return;
    if (!ensureRoot()) return;
    syncFromConfig();
  }

  // Robust bootstrap. The section's <script defer> can run BEFORE the section
  // markup exists (some themes load section JS eagerly / via AJAX), so a single
  // early init() would find no #NuceryBundleBuilder and bail permanently. Retry
  // on every ready signal + a short poll until the section appears and inits.
  function boot() { if (!window.__nuceryBBInit) init(); }
  boot();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  window.addEventListener('load', boot);
  document.addEventListener('shopify:section:load', boot);
  var bbTries = 0;
  var bbTimer = setInterval(function () {
    if (window.__nuceryBBInit || ++bbTries > 50) { clearInterval(bbTimer); return; }
    boot();
  }, 100);
})();
